import i18next from 'i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { toast } from 'react-toastify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { tableHelper } from 'common/helpers/table/table-helper';
import { StatusFilterDropdown } from 'common/partials/StatusFilterDropdown';
import {
    MasterLayout,
    MainLayout,
    ToolbarLayout,
    ToolbarLayoutLeft,
    ToolbarLayoutRight,
} from 'common/imports/content-layout';
import { DeleteModal } from 'common/partials/DeleteModal';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { RolesDictionary } from 'common/helpers/dictionaries/roles-dictionary';
import ProtectedComponent from 'common/partials/ProtectedComponent';
import ActionButton from 'common/partials/ActionButton';
import { StatusFilter } from 'common/types/status-filter.model';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { TenantManagementModel as Model } from '../model/tenant-management.model';
import { TenantManagementService as Service } from '../services/tenant-management.service';

export function TenantManagement() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<Model[]>([]);
    const [filteredTableData, setFilteredTableData] = useState<Model[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<Model[]>([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [deleteId, setDeleteId] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fetchData = async (status: StatusFilter = StatusFilter.Active) => {
        try {
            setIsLoading(true);
            const response = await Service.getTenantManagementList(status);
            if (response && !response.hasErrors) {
                setTableData(response.data);
                setFilteredTableData(response.data);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setIsLoading(false);
        }
    };

    function FilterByName(filter: string) {
        if (filter === '') {
            setFilteredTableData(tableData);
            return;
        }
        const normalizedFilter = filter.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        setFilteredTableData(
            tableData.filter((item) =>
                item.name
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()
                    .includes(normalizedFilter.toLowerCase()),
            ),
        );
    }

    const tenantDelete = (id: number) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const columns: TableColumn<Model>[] = [
        {
            name: `${t('tenant.tableHeaders.id')}`,
            selector: (row) => row.id || '',
            sortable: true,
        },
        {
            name: `${t('tenant.tableHeaders.name')}`,
            selector: (row) => row.name || '',
            sortable: true,
        },
        {
            name: `${t('tenant.tableHeaders.description')}`,
            selector: (row) => row.description || '',
            sortable: true,
        },
        {
            name: `${t('tenant.tableHeaders.color')}`,
            cell: (row) => (
                <div style={{ backgroundColor: row.color, width: 80, borderRadius: 15, textAlign: 'center' }}>&nbsp;</div>
            ),

            width: '100px',
        },
        {
            name: `${t('tenant.tableHeaders.status')}`,
            cell: (row) =>
                row.isActive ? (
                    <span className="badge badge-light-primary">{t('generalMessages.active')}</span>
                ) : (
                    <span className="badge badge-light-danger">{t('generalMessages.inactive')}</span>
                ),

            width: '150px',
        },
        {
            name: `${t('tenant.tableHeaders.actions')}`,
            cell: (row) => (
                <div className="d-grid d-flex gap-2">
                    <ActionButton
                        type="edit"
                        onClick={() => navigate(`/security/tenantmanagement/${row.id}`)}
                        roles={[RolesDictionary.Security.GlobalAdministrator]}
                    />
                    <ActionButton
                        type="deactivate"
                        onClick={() => tenantDelete(row.id!)}
                        roles={[RolesDictionary.Security.GlobalAdministrator]}
                        disabled={!row.isActive}
                    />
                </div>
            ),

            width: '100px',
        },
    ];

    const handleRowSelected = useCallback((state: any) => {
        setSelectedRows(state.selectedRows);
    }, []);

    const deleteDataById = async () => {
        try {
            setIsLoading(true);
            const result = await Service.deleteTenantById(deleteId);
            if (result && !result.hasErrors) {
                toast.success(`${t('crud.deactivate.successMessage')}`);
            } else {
                toast.error(`${t('crud.deactivate.errorMessage')}`);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setToggleCleared(!toggleCleared);
            setShowDeleteModal(false);
            fetchData();
            setIsLoading(false);
        }
    };

    const contextActions = useMemo(() => {
        const deleteSelectedRows = () => {
            setShowDeleteModal(true);
            selectedRows.forEach((row) => {
                setDeleteId(row.id!);
            });
        };

        return (
            <button type="button" className="btn btn-sm btn-danger" onClick={deleteSelectedRows}>
                {t('crud.deactivate.buttonSave')}
            </button>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, toggleCleared]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight>
                    <div className="d-flex align-items-center gap-2">
                        <StatusFilterDropdown handleFilterData={fetchData} />
                        <ProtectedComponent action="hide" role={[RolesDictionary.Security.GlobalAdministrator]}>
                            <Link to="/security/tenantmanagement/add" className="btn btn-sm btn-primary fw-bolder">
                                {t('tenant.toolbar.addClient')}
                            </Link>
                        </ProtectedComponent>
                    </div>
                </ToolbarLayoutRight>
            </ToolbarLayout>
            <MainLayout>
                <div className="card card-flush">
                    <div className="card-body">
                        <DataTable
                            title={tableHelper.TableHeader((e: any) => FilterByName(e.target.value))}
                            persistTableHead
                            columns={columns}
                            data={filteredTableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            selectableRows
                            selectableRowsHighlight
                            dense
                            highlightOnHover
                            pagination
                            paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                            paginationPerPage={tableHelper.defaultRowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            contextActions={contextActions}
                            contextMessage={tableHelper.ContextMessage()}
                            onSelectedRowsChange={handleRowSelected}
                            clearSelectedRows={toggleCleared}
                            selectableRowsVisibleOnly={true}
                            customStyles={itLabTableStyle}
                        />
                    </div>
                </div>

                <DeleteModal
                    type="deactivate"
                    handleDelete={deleteDataById}
                    onClose={() => setShowDeleteModal(false)}
                    open={showDeleteModal}
                />
            </MainLayout>
        </MasterLayout>
    );
}
